import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useMemo } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Modal } from 'shared/ui/Modal';
import { ServerTable } from 'shared/ui/ServerTable';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { getUserName, useGetUserBalanceActionsQuery, UserBalanceAction, useRole, UserPermissions } from 'entities/User';
import { ActionType } from 'entities/Invoice';
import { ReplenishBalance } from 'features/ReplenishBalance';
import { Button } from 'shared/ui/Button';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { ChargeBalance } from 'features/ChargeBalance';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';

interface UserBalanceModalProps {
  isOpened: boolean;
  userId: string;
  balance: number;
  onClose: () => void;
}

export const UserBalanceModal: FC<UserBalanceModalProps> = memo((props) => {
  const { isOpened, userId, onClose, balance } = props;

  const { t } = useAppTranslation(['profile', 'common']);

  const { isManagement } = useRole();

  const currencySymbol = useGetCurrencySymbol();

  const isForbiddenReplenishBalance = useCheckPermission(UserPermissions.REPLENISH_BALANCE);
  const isForbiddenChargeBalance = useCheckPermission(UserPermissions.CHARGE_BALANCE);

  const columns = useMemo<ColumnsType<UserBalanceAction>>(
    () => [
      {
        title: t('Date'),
        key: 'createdAt',
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.createdAt)}</div>,
      },
      {
        title: t('Action type'),
        key: 'actionType',
        render: (_, record) => {
          const isRefill = record.actionType === ActionType.REFILL;

          return <div className={isRefill ? 'text-success' : 'text-error'}>{t(record.actionType)}</div>;
        },
      },
      {
        title: t('Amount'),
        key: 'amount',
        render: (_, record) => {
          const isRefill = record.actionType === ActionType.REFILL;

          return (
            <div className={isRefill ? 'text-success' : 'text-error'}>
              {isRefill ? '+' : '-'}
              {record.amount} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Description'),
        key: 'description',
        render: (_, record) => {
          const splitedDescription = splitDocumentDescription(record.description || '');

          return (
            <div className="text-primary font-normal">
              {t(splitedDescription.description, { ns: 'common' })} {splitedDescription.documentNumber}
            </div>
          );
        },
      },
    ],
    [t, currencySymbol],
  );

  const extendedColumns: ColumnsType<UserBalanceAction> = [
    ...columns,
    {
      title: t('Created by'),
      key: 'issuedBy',
      render: (_, record) => <div className="text-primary font-normal">{getUserName(record?.issuedBy)}</div>,
    },
  ];

  return (
    <Modal width={980} isOpen={isOpened} onClose={onClose}>
      <div className="flex items-center space-x-8 py-3">
        <span className="text-accent text-xl font-semibold">
          {t('Balance: {{balance}}', { balance: balance ?? 0 })} {currencySymbol}
        </span>
        {isManagement && (
          <div className="flex gap-4 items-center">
            <ReplenishBalance
              userId={userId}
              action={
                <Button isDisabled={isForbiddenReplenishBalance} size="small">
                  {t('Replenish balance')}
                </Button>
              }
            />
            {balance > 0 && (
              <ChargeBalance
                userId={userId}
                balance={balance}
                action={
                  <Button isDisabled={isForbiddenChargeBalance} theme="danger" size="small">
                    {t('Charge balance')}
                  </Button>
                }
              />
            )}
          </div>
        )}
      </div>

      <ServerTable
        columns={isManagement ? extendedColumns : columns}
        fetchData={useGetUserBalanceActionsQuery}
        rowKey="userBalanceActionId"
        defaultFilters={{ userId }}
        title={t('User balance history')}
      />
    </Modal>
  );
});
